import React, { useEffect, useRef, useState } from 'react';
import { Statistic, theme, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { DEFAULT_TRANSPORT_CURRENCY } from './sharedTransportFormUtils';
import TransportRequiredFieldsChecklist from './TransportRequiredFieldsChecklist';
import type { MissingTransportRequiredField } from './transportRequiredFields';
import './TransportStickySummary.css';

const { Text } = Typography;

export const TRANSPORT_STICKY_OFFSET_BOTTOM = 16;
export const TRANSPORT_STICKY_SCROLL_CLEARANCE = 24;

interface FooterGeometry {
  left: number;
  width: number;
}

interface TransportStickySummaryProps {
  canViewTransportCosts: boolean;
  currency?: string;
  formatCurrencyAmount: (value: number | null | undefined) => string;
  totalGoodsValue: number | null | undefined;
  transportCostForAllocation: number;
  totalLandedCost: number;
  multiOrder: boolean;
  selectedOrderCount: number;
  submitButtons: React.ReactNode;
  missingRequiredFields: MissingTransportRequiredField[];
  onRequiredFieldClick: (field: MissingTransportRequiredField) => void;
  measuredHeight: number;
  onHeightChange?: (height: number) => void;
}

const TransportStickySummary: React.FC<TransportStickySummaryProps> = ({
  canViewTransportCosts,
  currency,
  formatCurrencyAmount,
  totalGoodsValue,
  transportCostForAllocation,
  totalLandedCost,
  multiOrder,
  selectedOrderCount,
  submitButtons,
  missingRequiredFields,
  onRequiredFieldClick,
  measuredHeight,
  onHeightChange,
}) => {
  const { t } = useTranslation(['transport']);
  const { token } = theme.useToken();
  const placeholderRef = useRef<HTMLDivElement>(null);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const [geometry, setGeometry] = useState<FooterGeometry | null>(null);

  useEffect(() => {
    const placeholder = placeholderRef.current;
    const surface = surfaceRef.current;
    if (!placeholder || !surface) return undefined;

    const updateGeometry = () => {
      const rect = placeholder.getBoundingClientRect();
      // On narrow screens the form sits inside several nested card paddings.
      // Reusing that inner width would turn the footer into a tall, unreadable
      // strip. The mobile footer belongs to the viewport; desktop keeps exact
      // alignment with the form surface.
      const next = window.innerWidth <= 767
        ? { left: 8, width: Math.max(0, window.innerWidth - 16) }
        : { left: rect.left, width: rect.width };
      setGeometry((current) =>
        current && current.left === next.left && current.width === next.width ? current : next
      );
      onHeightChange?.(Math.ceil(surface.getBoundingClientRect().height));
    };
    updateGeometry();

    const observer = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(updateGeometry);
    observer?.observe(placeholder);
    observer?.observe(surface);
    window.addEventListener('resize', updateGeometry);
    window.addEventListener('scroll', updateGeometry, true);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updateGeometry);
      window.removeEventListener('scroll', updateGeometry, true);
    };
  }, [onHeightChange]);

  return (
    <div
      ref={placeholderRef}
      className="transport-sticky-summary-placeholder"
    >
      <div
        data-testid="transport-sticky-summary-fixed"
        className="transport-sticky-summary-fixed"
        style={{
          left: geometry?.left,
          width: geometry?.width,
          visibility: geometry ? 'visible' : 'hidden',
        }}
      >
        <div
          ref={surfaceRef}
          role="region"
          aria-label={t('transport:requiredSummary.footerLabel')}
          data-testid="transport-sticky-summary"
          className="transport-sticky-summary"
          data-offset-bottom={TRANSPORT_STICKY_OFFSET_BOTTOM}
          data-measured-height={measuredHeight}
          style={{
            background: token.colorBgElevated,
            borderColor: token.colorBorderSecondary,
            boxShadow: `0 -4px 16px ${token.colorFillSecondary}`,
          }}
        >
          <div
            data-testid="transport-sticky-summary-layout"
            className="transport-sticky-summary__layout"
          >
            <div className="transport-sticky-summary__content">
              <TransportRequiredFieldsChecklist
                mode="compact"
                missingFields={missingRequiredFields}
                onFieldClick={onRequiredFieldClick}
              />
              {canViewTransportCosts ? (
                <div className="transport-sticky-summary__financials">
                  <div className="transport-sticky-summary__financial-item">
                    <Text type="secondary" className="transport-sticky-summary__label">{t('transport:costSummary.goodsValue')}</Text>
                    <Text>{formatCurrencyAmount(totalGoodsValue)}</Text>
                  </div>
                  <div className="transport-sticky-summary__financial-item">
                    <Text type="secondary" className="transport-sticky-summary__label">{t('transport:costSummary.transport')}</Text>
                    <Text>{formatCurrencyAmount(transportCostForAllocation)}</Text>
                  </div>
                  <div className="transport-sticky-summary__financial-item transport-sticky-summary__financial-total">
                    <Statistic
                      title={(
                        <Text type="secondary" className="transport-sticky-summary__label">
                          {t('transport:costSummary.total')}
                        </Text>
                      )}
                      value={totalLandedCost}
                      precision={2}
                      // Polish-first "4 500,00" (S36 follow-up, 2026-07-07): antd's
                      // Statistic defaults to en-US separators ("3,400.00").
                      decimalSeparator=","
                      groupSeparator=" "
                      suffix={currency || DEFAULT_TRANSPORT_CURRENCY}
                      valueStyle={{ color: token.colorPrimary, fontSize: 22, fontWeight: 700, lineHeight: 1.2 }}
                    />
                  </div>
                </div>
              ) : (
                <Text type="secondary" className="transport-sticky-summary__selection">
                  {selectedOrderCount > 0
                    ? t('transport:allocation.summary', { count: selectedOrderCount })
                    : t('transport:pleaseSelectAtLeastOneOrder')}
                </Text>
              )}
            </div>
            <div
              data-testid="transport-sticky-summary-actions"
              className="transport-sticky-summary__actions"
            >
              {submitButtons}
            </div>
          </div>
          {canViewTransportCosts && multiOrder ? (
            <Text type="secondary" className="transport-sticky-summary__hint">
              {t('transport:costSummary.perOrderHint', { count: selectedOrderCount })}
            </Text>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default TransportStickySummary;
